import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { MerchantApplication, ApplicationStatus, PipelineStatus } from '../types';

const ITEMS_PER_PAGE = 10;

export function MerchantApplicationsList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | 'all'>('all');
  const [pipelineFilter, setPipelineFilter] = useState<PipelineStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch all applications
  const { data: applications = [], isLoading } = useQuery({
    queryKey: ['merchantApplications', 'list'],
    queryFn: async () => {
      const q = query(
        collection(db, 'merchantApplications'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MerchantApplication[];
    },
  });

  // Filter applications
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Search filter
      const businessName = app.data?.corporationInfo?.businessName?.toLowerCase() || '';
      const email = app.data?.corporationInfo?.customerServiceEmail?.toLowerCase() || '';
      const phone = app.data?.corporationInfo?.customerServicePhone || '';
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = !searchTerm || 
        businessName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchLower);

      // Status filter
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;

      // Pipeline filter (case-insensitive matching)
      const matchesPipeline = pipelineFilter === 'all' ||
        app.pipelineStatus?.toLowerCase() === pipelineFilter?.toLowerCase();

      return matchesSearch && matchesStatus && matchesPipeline;
    });
  }, [applications, searchTerm, statusFilter, pipelineFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / ITEMS_PER_PAGE);
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getStatusBadge = (status: ApplicationStatus) => {
    const variants: Record<ApplicationStatus, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      not_started: { variant: 'secondary', label: 'Not Started' },
      in_progress: { variant: 'default', label: 'In Progress' },
      awaiting_signature: { variant: 'outline', label: 'Awaiting Signature' },
      submitted: { variant: 'default', label: 'Submitted' },
      approved: { variant: 'default', label: 'Approved' },
      rejected: { variant: 'destructive', label: 'Rejected' },
    };

    const config = variants[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPipelineBadge = (status: PipelineStatus) => {
    const variants: Record<PipelineStatus, { className: string; label: string }> = {
      lead: { className: 'bg-gray-100 text-gray-800', label: 'Lead' },
      phone: { className: 'bg-blue-100 text-blue-800', label: 'Phone' },
      offer: { className: 'bg-indigo-100 text-indigo-800', label: 'Offer' },
      underwriting: { className: 'bg-yellow-100 text-yellow-800', label: 'Underwriting' },
      documents: { className: 'bg-purple-100 text-purple-800', label: 'Documents' },
      approved: { className: 'bg-green-100 text-green-800', label: 'Approved' },
      onboarded: { className: 'bg-emerald-100 text-emerald-800', label: 'Onboarded' },
      live: { className: 'bg-green-200 text-green-900', label: 'Live' },
      processing: { className: 'bg-sky-100 text-sky-800', label: 'Processing' },
      declined: { className: 'bg-red-100 text-red-800', label: 'Declined' },
      failed_submission: { className: 'bg-pink-100 text-pink-800', label: 'Failed Submission' },
    };

    const config = variants[status] || { className: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (date: Date | { toDate: () => Date } | string | null) => {
    if (!date) return 'N/A';
    const d = typeof date === 'object' && 'toDate' in date 
      ? date.toDate() 
      : new Date(date as string | Date);
    return format(d, 'MMM d, yyyy');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>All Applications</CardTitle>
          <div className="text-sm text-muted-foreground">
            {filteredApplications.length} total applications
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by business name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as ApplicationStatus | 'all')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="awaiting_signature">Awaiting Signature</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={pipelineFilter} onValueChange={(value) => setPipelineFilter(value as PipelineStatus | 'all')}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by pipeline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="offer">Offer</SelectItem>
              <SelectItem value="underwriting">Underwriting</SelectItem>
              <SelectItem value="documents">Documents</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="onboarded">Onboarded</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pipeline Stage</TableHead>
                <TableHead>Date Created</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No applications found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedApplications.map((app) => (
                  <TableRow 
                    key={app.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/app/merchant-applications/${app.id}`)}
                  >
                    <TableCell className="font-medium">
                      {app.data?.corporationInfo?.businessName || 'Unnamed Business'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{app.data?.corporationInfo?.customerServiceEmail || 'No email'}</div>
                        <div className="text-muted-foreground">
                          {app.data?.corporationInfo?.customerServicePhone || 'No phone'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell>{getPipelineBadge(app.pipelineStatus)}</TableCell>
                    <TableCell>{formatDate(app.createdAt)}</TableCell>
                    <TableCell>{formatDate(app.updatedAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/merchant-applications/${app.id}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredApplications.length)} of{' '}
              {filteredApplications.length} applications
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className="w-9"
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
